import type { CatalogDocumentDTO } from '@/shared/api'
import type { RagScope } from './types'

/**
 * Build a {@link RagScope} that captures every available coordinate of a
 * catalogued document. Frontend-chat keeps scope at the document level for
 * now — entity/group are carried so the backend can still apply broader
 * filters when a future selector exposes them directly.
 */
export function scopeFromDocument(doc: CatalogDocumentDTO): RagScope {
  return {
    tenant_id: doc.tenant_id,
    entity_type: doc.entity_type || null,
    entity_id: doc.entity_id || null,
    group_type: doc.group_type ?? null,
    group_id: doc.group_id ?? null,
    document_id: doc.document_id,
    document_filename: doc.filename ?? null,
    document_title: doc.filename ?? doc.document_id,
  }
}

/** Stable, human-readable identifier — used in selector rows and badges.
 *
 * Step 9.5 — prefer the friendly ``title`` when set, otherwise fall back to
 * the original filename and finally the document_id slug. The entity/group
 * display names are surfaced elsewhere (DocumentCard subtitle).
 */
export function documentDisplayName(doc: CatalogDocumentDTO): string {
  if (doc.title && doc.title.trim()) return doc.title.trim()
  return doc.filename ?? doc.document_id
}

/**
 * Stable identity for a catalog row across re-fetches. Used by the scope
 * provider to dedupe multi-doc selections and prune entries that no longer
 * exist after a catalog reload. Matches the same coordinate tuple the
 * backend uses for uniqueness — bare ``document_id`` is not enough because
 * the same id can repeat across (entity, group) tuples.
 */
export function documentKey(doc: CatalogDocumentDTO): string {
  return [
    doc.tenant_id,
    doc.entity_type ?? '',
    doc.entity_id ?? '',
    doc.group_type ?? '',
    doc.group_id ?? '',
    doc.document_id,
  ].join('::')
}

/** Short summary line used by the composer/badge. */
export function scopeSummary(scope: RagScope | null): string {
  if (!scope) return 'Todo o workspace'
  const parts: string[] = []
  if (scope.entity_id) {
    parts.push(scope.entity_id)
  }
  if (scope.group_id) {
    parts.push(scope.group_id)
  }
  const doc = scope.document_title ?? scope.document_filename ?? scope.document_id
  if (doc) {
    parts.push(doc)
  }
  return parts.length > 0 ? parts.join(' / ') : 'Escopo selecionado'
}

/**
 * Multi-doc summary line used by the composer/badge. Step 8.3 introduces
 * multi-case selection, so the badge needs to reflect "N documentos" while
 * keeping the single-doc UX (just the filename) crisp.
 */
export function multiCaseSummary(docs: CatalogDocumentDTO[]): string {
  // Step 13.7 — "nenhum documento selecionado" em vez de "Todo o workspace":
  // após o Step 13.5 o backend NÃO faz mais varredura tenant-wide; uma pergunta
  // sem escopo selecionado retorna no_context. O rótulo antigo era enganoso.
  if (docs.length === 0) return 'nenhum documento selecionado'
  if (docs.length === 1) return documentDisplayName(docs[0])
  const first = documentDisplayName(docs[0])
  const extra = docs.length - 1
  return `${first} + ${extra} documento${extra === 1 ? '' : 's'}`
}

/** True when the catalogued document is ready for retrieval. */
export function isDocumentRetrievable(doc: CatalogDocumentDTO): boolean {
  if (doc.chunk_count <= 0) return false
  // Postgres backend tags status=indexed; Chroma backend leaves it null but
  // only returns indexed documents to begin with, so null is treated as ok.
  return doc.status === null || doc.status === 'indexed'
}

/** Reason a document cannot be selected, or null when it's selectable. */
export function unavailableReason(doc: CatalogDocumentDTO): string | null {
  if (doc.chunk_count <= 0) {
    return 'Documento ainda sem chunks indexados.'
  }
  if (doc.status && doc.status !== 'indexed') {
    return `Status atual: ${doc.status}.`
  }
  return null
}

/** Compare two scopes structurally; ``null`` == ``null`` means same scope. */
export function scopeEquals(
  a: RagScope | null,
  b: RagScope | null,
): boolean {
  if (a === null && b === null) return true
  if (!a || !b) return false
  return (
    a.tenant_id === b.tenant_id &&
    (a.entity_type ?? null) === (b.entity_type ?? null) &&
    (a.entity_id ?? null) === (b.entity_id ?? null) &&
    (a.group_type ?? null) === (b.group_type ?? null) &&
    (a.group_id ?? null) === (b.group_id ?? null) &&
    (a.document_id ?? null) === (b.document_id ?? null)
  )
}
